import React, { LegacyRef, useEffect, useRef } from 'react';
import { Box, HStack, Link, VStack, useMergeRefs } from '@chakra-ui/react';
import { AnimateSharedLayout } from 'framer-motion';
import { MotionBox } from '@olo-web/components/atoms/motion/MotionBox.ui';
import { useInView } from 'react-intersection-observer';
import { useRibbonScroll } from './useRibbonScroll';

const MENU_CATEGORIES_NAV_LABEL = 'Menu categories';

function RibbonNavItem({
  item,
  isSelected,
  onActivate,
  itemRef,
}: {
  item: IRibbonItem;
  isSelected: boolean;
  onActivate: (group: IRibbonItem) => void;
  itemRef: (ref: HTMLElement | null) => void;
}): JSX.Element {
  const internalItemRef = useRef<HTMLElement>(null);
  const { inView, ref } = useInView();
  const refs = useMergeRefs(internalItemRef, ref);
  const fragmentId = `group-menu-${item.value}`;

  useEffect(() => {
    if (internalItemRef.current) {
      itemRef(internalItemRef.current);
    }
  }, [itemRef]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    onActivate(item);
    if (isSelected && !inView) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      internalItemRef?.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  return (
    <Box as="li" listStyleType="none" ref={refs} flexShrink={0}>
      <VStack pos="relative" spacing={0} w="max-content">
        <Link
          variant="unstyled"
          href={`#${fragmentId}`}
          onClick={handleClick}
          textStyle="p"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          minH="42px"
          px={2}
          whiteSpace="nowrap"
          color={isSelected ? 'black' : 'blackAlpha.500'}
          fontWeight={isSelected ? '500' : '300'}
          h="42px"
          data-testid="menu-item-group"
          aria-current={isSelected ? true : undefined}
          _hover={{ color: 'primary.350' }}
          _focusVisible={{
            outline: '2px solid',
            outlineColor: 'primary.350',
            outlineOffset: '2px',
          }}
        >
          {item.label}
        </Link>
        {isSelected && (
          <MotionBox
            layoutId="underline"
            w="100%"
            h="4px"
            bg="primary.350"
            pos="absolute"
            bottom={{ base: 0, md: '1px' }}
            borderRadius="5px 5px 0 0"
          />
        )}
      </VStack>
    </Box>
  );
}

export function Ribbon({
  items = [],
  onSelect,
  value,
  refItem,
}: {
  items: IRibbonItem[];
  onSelect: (item: IRibbonItem) => void;
  value?: any;
  refItem: LegacyRef<HTMLDivElement>;
}): JSX.Element {
  const { ribbonContainerRef, setTabRef } = useRibbonScroll({ value, items });

  return (
    <HStack
      ref={ribbonContainerRef}
      pos="absolute"
      bottom={0}
      right={{
        base: 8,
        md: 20,
      }}
      left={{
        base: 0,
        md: 8,
      }}
      overflowX="scroll"
      h="44px"
    >
      <HStack
        spacing={0}
        px={2}
        pos="relative"
        h="44px"
        data-testid="menu-item-group-list"
        ref={refItem}
      >
        <Box
          as="nav"
          aria-label={MENU_CATEGORIES_NAV_LABEL}
          display="flex"
          alignItems="stretch"
          flexShrink={0}
          w="max-content"
        >
          <AnimateSharedLayout>
            <Box
              as="ul"
              display="flex"
              flexDirection="row"
              alignItems="stretch"
              gap={2}
              listStyleType="none"
              margin={0}
              padding={0}
              flexShrink={0}
              w="max-content"
            >
              {items.map((item) => (
                <RibbonNavItem
                  key={item.value}
                  item={item}
                  onActivate={onSelect}
                  isSelected={value === item.value}
                  itemRef={setTabRef(item.value)}
                />
              ))}
            </Box>
          </AnimateSharedLayout>
        </Box>
      </HStack>
    </HStack>
  );
}
